import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  AlertCircle,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Loader2,
  Sparkles,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import {
  useActivateSubscriptionNow,
  usePreviewTrialActivation,
  type ActivateSubscriptionNowSuccess,
} from '@/api/stripe'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type Step = 'confirm' | 'success'

/**
 * Modale d'activation immédiate de l'abonnement (sortie volontaire d'essai).
 * Trois états visuels :
 *  - confirm : montre montant TTC + CB, demande confirmation
 *  - loading : pendant l'appel Stripe (état imbriqué dans confirm via isPending)
 *  - success : facture créée + lien vers la facture PDF
 *
 * Échec CB → toast + modale fermée + trial intact (gestion côté hook).
 */
export function EndTrialNowDialog({ open, onOpenChange }: Props) {
  const { t, i18n } = useTranslation()
  const darkMode = useUIStore((s) => s.darkMode)
  const [step, setStep] = useState<Step>('confirm')
  const [result, setResult] = useState<ActivateSubscriptionNowSuccess | null>(null)

  const preview = usePreviewTrialActivation(open)
  const activate = useActivateSubscriptionNow()

  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setStep('confirm')
        setResult(null)
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [open])

  useEffect(() => {
    if (open && preview.isSuccess && preview.data && !preview.data.has_payment_method) {
      try {
        void import('posthog-js').then((m) =>
          m.default.capture('trial_activate_dialog_no_payment_method'),
        )
      } catch {
        // posthog optional
      }
    }
  }, [open, preview.isSuccess, preview.data])

  useEffect(() => {
    if (open && preview.isSuccess) {
      try {
        void import('posthog-js').then((m) =>
          m.default.capture('trial_activate_dialog_opened', {
            amount_total: preview.data?.amount_total,
            currency: preview.data?.currency,
            plan: preview.data?.plan,
          }),
        )
      } catch {
        // posthog optional
      }
    }
    // On ne veut tracker qu'à la première réception du preview, pas à chaque render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preview.isSuccess])

  const formatMoney = (cents: number, currency: string) => {
    const value = cents / 100
    const locale = i18n.language === 'fr' ? 'fr-CA' : 'en-CA'
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(value)
  }

  const handleConfirm = async () => {
    try {
      const data = await activate.mutateAsync()
      setResult(data)
      setStep('success')
      try {
        void import('posthog-js').then((m) =>
          m.default.capture('trial_activated_early', {
            amount_paid: data.amount_paid,
            plan: data.plan,
          }),
        )
      } catch {
        // posthog optional
      }
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: { error?: string; status?: string; decline_code?: string | null } }
      }
      const apiError = error.response?.data
      const errorMessage =
        apiError?.error ||
        t(
          'manager.billing.activateNow.errorGeneric',
          'Une erreur est survenue. Veuillez réessayer.',
        )

      toast.error(errorMessage, {
        description:
          apiError?.status === 'payment_failed'
            ? t(
                'manager.billing.activateNow.errorCardHint',
                'Mettez à jour votre carte dans la section Méthode de paiement.',
              )
            : undefined,
      })

      try {
        void import('posthog-js').then((m) =>
          m.default.capture('trial_activation_failed', {
            status: apiError?.status,
            decline_code: apiError?.decline_code,
          }),
        )
      } catch {
        // posthog optional
      }

      onOpenChange(false)
    }
  }

  const isLoadingPreview = preview.isPending
  const previewError = preview.isError
  const data = preview.data
  const hasPaymentMethod = data?.has_payment_method ?? false
  const isProcessing = activate.isPending

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (isProcessing) return
        onOpenChange(next)
      }}
    >
      <DialogContent
        className={cn(
          'max-w-md',
          darkMode && 'bg-slate-900 border-slate-700 text-white',
        )}
      >
        {step === 'confirm' && (
          <>
            <DialogHeader>
              <div
                className={cn(
                  'mx-auto flex h-12 w-12 items-center justify-center rounded-full mb-2',
                  darkMode ? 'bg-blue-900/30' : 'bg-blue-50',
                )}
              >
                <Sparkles
                  className={cn(
                    'h-6 w-6',
                    darkMode ? 'text-blue-400' : 'text-blue-600',
                  )}
                />
              </div>
              <DialogTitle className="text-center">
                {t(
                  'manager.billing.activateNow.title',
                  'Activer mon abonnement aujourd\'hui',
                )}
              </DialogTitle>
              <DialogDescription
                className={cn('text-center', darkMode && 'text-slate-400')}
              >
                {t(
                  'manager.billing.activateNow.subtitle',
                  'Terminez votre essai et passez en mode payant dès maintenant pour retirer toutes les limites.',
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-2">
              {isLoadingPreview ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : previewError ? (
                <div
                  className={cn(
                    'rounded-lg border p-3 flex items-start gap-2',
                    darkMode
                      ? 'bg-red-950/30 border-red-800/50 text-red-300'
                      : 'bg-red-50 border-red-200 text-red-700',
                  )}
                >
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">
                    {t(
                      'manager.billing.activateNow.previewError',
                      'Impossible de calculer le montant. Veuillez réessayer.',
                    )}
                  </p>
                </div>
              ) : data ? (
                <>
                  <div
                    className={cn(
                      'rounded-lg border p-4 space-y-2',
                      darkMode
                        ? 'bg-slate-800/50 border-slate-700'
                        : 'bg-slate-50 border-slate-200',
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={cn(
                          'text-sm',
                          darkMode ? 'text-slate-400' : 'text-slate-600',
                        )}
                      >
                        {t('manager.billing.activateNow.plan', 'Plan')}
                      </span>
                      <span className="text-sm font-medium">
                        {data.plan_display}
                      </span>
                    </div>
                    {data.amount_tax > 0 && (
                      <>
                        <div className="flex items-center justify-between">
                          <span
                            className={cn(
                              'text-sm',
                              darkMode ? 'text-slate-400' : 'text-slate-600',
                            )}
                          >
                            {t(
                              'manager.billing.activateNow.subtotal',
                              'Sous-total',
                            )}
                          </span>
                          <span className="text-sm">
                            {formatMoney(data.amount_subtotal, data.currency)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span
                            className={cn(
                              'text-sm',
                              darkMode ? 'text-slate-400' : 'text-slate-600',
                            )}
                          >
                            {t('manager.billing.activateNow.tax', 'Taxes')}
                          </span>
                          <span className="text-sm">
                            {formatMoney(data.amount_tax, data.currency)}
                          </span>
                        </div>
                      </>
                    )}
                    <div
                      className={cn(
                        'flex items-center justify-between pt-2 border-t',
                        darkMode ? 'border-slate-700' : 'border-slate-200',
                      )}
                    >
                      <span className="text-sm font-semibold">
                        {t('manager.billing.activateNow.total', 'Total aujourd\'hui')}
                      </span>
                      <span className="text-lg font-bold">
                        {formatMoney(data.amount_total, data.currency)}
                      </span>
                    </div>
                  </div>

                  {hasPaymentMethod && data.payment_method ? (
                    <div
                      className={cn(
                        'flex items-center gap-2 text-sm',
                        darkMode ? 'text-slate-400' : 'text-slate-600',
                      )}
                    >
                      <CreditCard className="h-4 w-4" />
                      <span>
                        {t(
                          'manager.billing.activateNow.chargedOn',
                          'Sera prélevé sur {{brand}} •••• {{last4}}',
                          {
                            brand: data.payment_method.brand.toUpperCase(),
                            last4: data.payment_method.last4,
                          },
                        )}
                      </span>
                    </div>
                  ) : (
                    <div
                      className={cn(
                        'rounded-lg border p-3 flex items-start gap-2',
                        darkMode
                          ? 'bg-amber-950/30 border-amber-800/50 text-amber-300'
                          : 'bg-amber-50 border-amber-200 text-amber-800',
                      )}
                    >
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <p className="text-sm">
                        {t(
                          'manager.billing.activateNow.noPaymentMethod',
                          'Aucune méthode de paiement enregistrée. Ajoutez une carte dans Facturation avant d\'activer.',
                        )}
                      </p>
                    </div>
                  )}

                  <p
                    className={cn(
                      'text-xs',
                      darkMode ? 'text-slate-500' : 'text-slate-500',
                    )}
                  >
                    {t(
                      'manager.billing.activateNow.disclaimer',
                      'Vos jours d\'essai restants seront convertis en accès illimité dès aujourd\'hui. Cette action est définitive.',
                    )}
                  </p>
                </>
              ) : null}
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isProcessing}
                className={darkMode ? 'border-slate-700' : ''}
              >
                {t('common.cancel', 'Annuler')}
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={
                  isProcessing ||
                  isLoadingPreview ||
                  previewError ||
                  !hasPaymentMethod
                }
                className="bg-[#0A192F] hover:bg-[#0A192F]/90 text-white dark:bg-white dark:text-[#0A192F] dark:hover:bg-white/90"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t(
                      'manager.billing.activateNow.processing',
                      'Traitement…',
                    )}
                  </>
                ) : data ? (
                  t(
                    'manager.billing.activateNow.confirm',
                    'Payer {{amount}} et activer',
                    { amount: formatMoney(data.amount_total, data.currency) },
                  )
                ) : (
                  t('manager.billing.activateNow.confirmShort', 'Activer')
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'success' && result && (
          <>
            <DialogHeader>
              <div
                className={cn(
                  'mx-auto flex h-12 w-12 items-center justify-center rounded-full mb-2',
                  darkMode ? 'bg-emerald-900/30' : 'bg-emerald-50',
                )}
              >
                <CheckCircle2
                  className={cn(
                    'h-6 w-6',
                    darkMode ? 'text-emerald-400' : 'text-emerald-600',
                  )}
                />
              </div>
              <DialogTitle className="text-center">
                {t(
                  'manager.billing.activateNow.successTitle',
                  'Abonnement activé !',
                )}
              </DialogTitle>
              <DialogDescription
                className={cn('text-center', darkMode && 'text-slate-400')}
              >
                {result.message}
              </DialogDescription>
            </DialogHeader>

            {result.amount_paid !== null && (
              <div
                className={cn(
                  'rounded-lg border p-4 text-center',
                  darkMode
                    ? 'bg-slate-800/50 border-slate-700'
                    : 'bg-slate-50 border-slate-200',
                )}
              >
                <p
                  className={cn(
                    'text-xs uppercase tracking-wide mb-1',
                    darkMode ? 'text-slate-400' : 'text-slate-500',
                  )}
                >
                  {t('manager.billing.activateNow.amountPaid', 'Montant payé')}
                </p>
                <p className="text-2xl font-bold">
                  {formatMoney(result.amount_paid, 'CAD')}
                </p>
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-2">
              {result.invoice_hosted_url && (
                <Button
                  variant="outline"
                  asChild
                  className={darkMode ? 'border-slate-700' : ''}
                >
                  <a
                    href={result.invoice_hosted_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {t(
                      'manager.billing.activateNow.viewInvoice',
                      'Voir la facture',
                    )}
                  </a>
                </Button>
              )}
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-[#0A192F] hover:bg-[#0A192F]/90 text-white dark:bg-white dark:text-[#0A192F] dark:hover:bg-white/90"
              >
                {t('common.close', 'Fermer')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
